/**
 * Pure logic for the Contacts + Identity + Onboarding UI (no React, no RPC —
 * fully unit-testable). Design: ai-docs/lexiun-contacts.md §2/§4.1/§6.2/§7A +
 * D-9/D-13.
 */
import type {
  Contact,
  ContactDraft,
  OwnerIdentityDraft,
  SetupStatus,
  SetupStepId,
  SetupStepItem,
  TrustTier,
} from "@/types/contacts";
import { OWNER_CONTACT_ID } from "@/types/contacts";

/**
 * The platform default assistant name — the assistant's PERSONAL name, never
 * the product brand "Lexiun" (operator 2026-07-27; runtime counterpart
 * `DEFAULT_ASSISTANT_NAME` in `contacts/assistant-identity-store.ts`, shipped
 * by the §9.3a handback).
 *
 * Lives HERE, in the pure-logic module, so both this file's previews and the
 * React side (`hooks/useAssistantIdentity`, which re-exports it) read one
 * constant — a preview that signs a different name than the hero displays is
 * the W-3 contradiction this constant exists to prevent.
 */
export const ASSISTANT_DEFAULT_NAME = "Alex";

// ── multi-value editor invariants ───────────────────────────────────────────

/** Set entry `index` as the single `primary` of a multi-value list (§2: all
 *  groups carry a `primary` flag — at most one). Returns a new array. */
export function setPrimary<T extends { primary?: boolean }>(list: T[], index: number): T[] {
  return list.map((entry, i) => {
    const next = { ...entry };
    if (i === index) next.primary = true;
    else delete next.primary;
    return next;
  });
}

/** Remove entry `index`; if it was the primary, promote the first remaining
 *  entry so a non-empty list always keeps exactly one primary. */
export function removeEntry<T extends { primary?: boolean }>(list: T[], index: number): T[] {
  const removed = list[index];
  const next = list.filter((_, i) => i !== index);
  if (removed?.primary && next.length > 0 && !next.some((e) => e.primary)) {
    return setPrimary(next, 0);
  }
  return next;
}

/** Append an entry; the first entry of a list becomes primary automatically. */
export function addEntry<T extends { primary?: boolean }>(list: T[], entry: T): T[] {
  const next = [...list, entry];
  return list.length === 0 ? setPrimary(next, 0) : next;
}

// ── §4.1 tier metadata (plain-language, user-facing — pt-BR first) ──────────

export interface TierMeta {
  tier: TrustTier;
  /** Short badge label. */
  label: string;
  /** The plain-language meaning restated on selection AND in the confirm. */
  description: string;
  /** Sensitive tiers demand the strongest confirm (CT-INV-7/8). */
  sensitive: boolean;
}

export const TIER_META: Record<TrustTier, TierMeta> = {
  owner: {
    tier: "owner",
    label: "Titular",
    description:
      "Você. Acesso completo por mensagem: casos, troca de escopo, todas as ferramentas. Somente as SUAS identidades verificadas ficam neste nível.",
    sensitive: true,
  },
  trusted: {
    tier: "trusted",
    label: "Confiança",
    description:
      "Pessoa elevada por você: conversa com o assistente e deixa recados/arquivos, mas NUNCA acessa dados de casos.",
    sensitive: true,
  },
  known: {
    tier: "known",
    label: "Conhecido",
    description:
      "Contato identificado (padrão). Modo recepcionista: o assistente acolhe, anota o recado e avisa você — nunca responde questões do caso nem confirma que um caso existe.",
    sensitive: false,
  },
  unknown: {
    tier: "unknown",
    label: "Desconhecido",
    description:
      "Remetente não identificado: resposta padrão fixa + o recado é estacionado até você decidir como tratar.",
    sensitive: false,
  },
  blocked: {
    tier: "blocked",
    label: "Bloqueado",
    description: "Silêncio total. Mensagens são apenas registradas, nunca respondidas.",
    sensitive: false,
  },
};

/** Does changing `from` → `to` require the explicit restate-the-meaning
 *  confirm? Any change INTO a sensitive tier, or any change AWAY from
 *  owner/blocked (loosening/releasing), does. */
export function tierChangeNeedsConfirm(from: TrustTier, to: TrustTier): boolean {
  if (from === to) return false;
  return TIER_META[to].sensitive || from === "owner" || from === "blocked";
}

// ── D-9 assistant-identity guardrails (mirror of the runtime refine) ────────

const COUNSEL_STYLING = /\b(dr|dra|doutor|doutora)\b/i;
const OAB_REF = /\boab\b/i;

/** The assistant name is an ALIAS — a first name, not a full name (operator
 *  ruling 2026-07-27). One word, letters only, ≤15 chars, initial-cap. */
export const ASSISTANT_ALIAS_MAX = 15;

/** Letters ONLY (Unicode, so "Iuná"/"José" are fine) — no spaces, digits,
 *  punctuation or symbols. */
const ALIAS_SHAPE = /^\p{L}+$/u;

/** Normalize an alias to its canonical display form: initial cap, rest lower.
 *  Applied as the user types so what they see is exactly what gets stored —
 *  a format rule the user has to satisfy by hand is a worse rule. */
export function formatAssistantAlias(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  return t.charAt(0).toLocaleUpperCase("pt-BR") + t.slice(1).toLocaleLowerCase("pt-BR");
}

/** Returns the user-facing violation message, or null when the name passes.
 *  `takenNames` = real people's names from the org (D-9: never a real
 *  person's name from the org's contacts/users). */
export function assistantNameViolation(
  name: string,
  takenNames: readonly string[] = [],
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "O nome não pode ficar vazio.";
  // Shape BEFORE length: "Maria Silva" should read as "one word", not as a
  // length problem, since the length message would send the user to trim it.
  if (!ALIAS_SHAPE.test(trimmed)) {
    return "Use uma única palavra, só letras — sem espaços, números ou símbolos (é um apelido, como um primeiro nome).";
  }
  if (trimmed.length > ASSISTANT_ALIAS_MAX) {
    return `Máximo de ${ASSISTANT_ALIAS_MAX} caracteres.`;
  }
  if (COUNSEL_STYLING.test(trimmed) || OAB_REF.test(trimmed)) {
    return "O assistente nunca se apresenta como advogado(a) — sem “Dr./Dra.” nem OAB (regra D-9).";
  }
  // Compare against each taken name AND its individual word tokens. Now that
  // the alias must be ONE word, a whole-string match could never fire against a
  // multi-word owner ("Alexandre Moraes"), which is most of them — so the
  // real-person half of D-9 would have silently died with the alias rule. An
  // alias of "Alexandre" or "Moraes" is exactly the case it exists to stop.
  const lower = trimmed.toLowerCase();
  const taken = new Set<string>();
  for (const n of takenNames) {
    const full = n.trim().toLowerCase();
    if (!full) continue;
    taken.add(full);
    for (const part of full.split(/\s+/)) if (part) taken.add(part);
  }
  if (taken.has(lower)) {
    return "Esse é o nome de uma pessoa real dos seus contatos/usuários — escolha um nome próprio para o assistente (regra D-9).";
  }
  return null;
}

/** Live signature preview (§6.2 — how the assistant signs IM replies and
 *  receptionist templates). */
export function assistantSignaturePreview(assistantName: string, ownerName: string): string {
  // The platform default is the assistant's PERSONAL name (Alex), never the
  // product brand — a preview that signed "Lexiun" while the hero says "Alex"
  // is exactly the W-3 contradiction (handback §9.3a(a), 2026-07-27).
  const a = assistantName.trim() || ASSISTANT_DEFAULT_NAME;
  const o = ownerName.trim() || "seu advogado(a)";
  return `Sou ${a}, assistente do escritório de ${o} — vou repassar seu recado.`;
}

/** Email From preview: display name + the agent's own dedicated address
 *  (`{UserName}` interpolation per §6.2).
 *
 *  DEFAULT FORMAT (operator 2026-07-28, superseding the `Alex - {UserName}`
 *  form ruled a day earlier): `Alex via Lexiun` — the owner is NOT in the
 *  default at all. This preview must MATCH what lexserver actually emits when
 *  no template is set (`mailsystem` `c19696f69`); a preview that shows a
 *  different sender than the one that lands in the inbox is a lie, not a hint.
 *
 *  Both retired forms failed the same way — they put the lawyer in a header
 *  that is not about the lawyer. `em nome de` was worse still: "on behalf of"
 *  is a real RFC 5322 mechanism (`From:` = the author, `Sender:` = the
 *  transmitting agent, which clients render as "X on behalf of Y") and we
 *  deliberately do NOT use it — `From` stays the agent's own address for DMARC
 *  alignment, and D-10 makes the assistant a DISTINCT correspondent rather
 *  than the lawyer. `via` is the established convention and claims no
 *  authorship.
 *
 *  An explicit `emailDisplayName` template still WINS and may name the owner
 *  through `{UserName}`, so a firm wanting the on-behalf form keeps it.
 *
 *  ⚠ Character hazards live on the TEMPLATE path only, now that the owner name
 *  is out of the default: `(` `)` `[` `]` are RFC 5322 `specials`, legal in a
 *  display-name only inside a quoted-string, and AgentMail hard-rejects
 *  parentheses (`DISPLAY_NAME_PARENTHESES`). An owner name carrying them can
 *  no longer poison the default — it can still poison a template that
 *  interpolates it. */
export function assistantEmailFromPreview(
  displayNameTemplate: string | undefined,
  assistantName: string,
  ownerName: string,
  address: string | undefined,
): string {
  const template =
    displayNameTemplate?.trim() ||
    `${assistantName.trim() || ASSISTANT_DEFAULT_NAME} via Lexiun`;
  const display = template.replaceAll("{UserName}", ownerName.trim() || "—");
  return address ? `${display} <${address}>` : display;
}

// ── Identity auto-save (operator directive 2026-07-17) ──────────────────────

/** Serialize EXACTLY the identity.set writable fields — the auto-save dirty
 *  baseline. Pure + deterministic (key order fixed by construction). */
export function serializeIdentityDraft(
  owner: Pick<Contact, "displayName" | "treatment" | "language" | "emails" | "phones"> | null,
  assistant: {
    name: string;
    emoji?: string;
    theme?: string;
    avatar?: string;
    emails?: unknown;
    emailDisplayName?: string;
  } | null,
): string {
  if (!owner || !assistant) return "";
  return JSON.stringify([
    owner.displayName,
    owner.treatment ?? null,
    owner.language ?? null,
    owner.emails,
    owner.phones,
    assistant.name,
    assistant.emoji ?? null,
    assistant.theme ?? null,
    assistant.avatar ?? null,
    assistant.emails ?? null,
    assistant.emailDisplayName ?? null,
  ]);
}

// ── Agent identities pane (operator directive 2026-07-17) ───────────────────

/** The assistant's PROVISIONED mailbox address: the setup evaluation's
 *  `assistant-mailbox` detail is the provisioning SoT (the creds-store
 *  address); the identity record's plural emails are aliases/fallback. Pure. */
export function agentMailboxAddress(
  items: readonly Pick<SetupStepItem, "id" | "done" | "detail">[] | undefined,
  assistantEmails: readonly { address: string; primary?: boolean }[] | undefined,
): string | null {
  const provisioned = items?.find((i) => i.id === "assistant-mailbox" && i.done)?.detail;
  if (provisioned?.includes("@")) return provisioned;
  const primary = assistantEmails?.find((e) => e.primary) ?? assistantEmails?.[0];
  return primary?.address ?? null;
}

// ── D-13 reminder mechanics (pure decision; timestamps live runtime-side) ───

export const REMINDER_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h between re-displays
export const REMINDER_DISABLE_RESET_MS = 7 * 24 * 60 * 60 * 1000; // disable auto-resets after 7d

/** Decide whether the login-time setup reminder should be displayed NOW.
 *  Rules (operator-set 2026-07-16): incomplete profile → re-display on login
 *  with a ≥6h interval; explicit disable suppresses it but auto-resets after
 *  7 days. Pure function of the runtime-persisted status + the clock. */
export function shouldShowSetupReminder(status: SetupStatus, nowMs: number): boolean {
  if (status.complete) return false;
  if (status.reminderDisabledAt) {
    const disabledAt = Date.parse(status.reminderDisabledAt);
    if (Number.isFinite(disabledAt) && nowMs - disabledAt < REMINDER_DISABLE_RESET_MS) {
      return false; // explicit "não me lembrar" still in force
    }
  }
  if (status.reminderLastShownAt) {
    const lastShown = Date.parse(status.reminderLastShownAt);
    if (Number.isFinite(lastShown) && nowMs - lastShown < REMINDER_MIN_INTERVAL_MS) {
      return false; // shown within the 6h window
    }
  }
  return true;
}

/** Checklist display order (the v2 registry ids; user-setup design §2). */
export const SETUP_ITEM_ORDER: readonly SetupStepId[] = [
  "owner-name",
  // ⚖ Immediately after `owner-name`, and the position is load-bearing rather
  // than cosmetic. The IM door (below) asks the lawyer to name his Telegram bot
  // after his assistant — so reaching it with no assistant named leaves him
  // inventing a name against an abstract rule, which is how a lawyer named
  // Alexandre came to pick "Alex" and have his own clients messaged by his
  // nickname. Naming comes first so the BotFather step has a string to hand him.
  //
  // ⚠ This array is the ONLY thing that decides display order:
  // `OnboardingChecklist.tsx:142` maps over it with NO tier sort, and
  // `setup-wizard.ts:35-44` resolves the wizard's sequence through it. The
  // backend registry's tier is INDEPENDENT of position — retiering a step
  // changes its badge and never where it appears. Both halves are required,
  // which is why this moved alongside `lexiun-user-setup`'s retier rather than
  // before or after it.
  // Design: ai-docs/handoffs/assistant-name-early-step-design-2026-09-14.md
  "assistant-name",
  "owner-email",
  "im-door",
  "im-owner-pairing",
  "telegram-owner-pairing",
  "assistant-mailbox",
  "notifications",
  "whatsapp-door",
  // Endpoint 2 — MUST be listed, or the checklist (which maps over this order)
  // silently drops it and the lawyer never learns the channel is ownerless.
  "whatsapp-owner-pairing",
  "email-rules",
];

/** The blocking gate decision (user-setup §5.1 — DS-2 uniform enforcement):
 *  outstanding required-blocking steps ⇒ route into the setup surface. Pure. */
export function shouldGateSetup(status: SetupStatus): boolean {
  return (status.requiredBlockingOutstanding?.length ?? 0) > 0;
}

/** Route prefixes that stay reachable WHILE gated (US-INV-7: the gate must
 *  contain its own exit — identity/wizards/settings; everything else routes
 *  back to the setup surface). */
export const SETUP_GATE_ALLOWED_PREFIXES: readonly string[] = [
  "/identity",
  "/channels",
  "/settings",
];

export function isSetupGateAllowedPath(pathname: string): boolean {
  return SETUP_GATE_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// ── list filtering (client-side refinement over contacts.list) ──────────────

export interface ContactsFilter {
  query?: string;
  relationship?: string;
  tag?: string;
  clientId?: string;
  trustTier?: TrustTier;
  includeArchived?: boolean;
}

export function filterContacts(contacts: readonly Contact[], f: ContactsFilter): Contact[] {
  const q = f.query?.trim().toLowerCase();
  return contacts.filter((c) => {
    if (!f.includeArchived && c.status === "archived") return false;
    if (f.relationship && c.relationship !== f.relationship) return false;
    if (f.trustTier && c.trustTier !== f.trustTier) return false;
    if (f.tag && !c.tags.includes(f.tag)) return false;
    if (f.clientId && c.clientId !== f.clientId) return false;
    if (q) {
      const hay = [
        c.displayName,
        c.fullLegalName,
        c.organizationName,
        ...c.emails.map((e) => e.address),
        ...c.phones.map((p) => p.number),
      ]
        .filter(Boolean)
        .join("\n")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** §4.4 — contacts parked awaiting the lawyer's disposition answer
 *  (first-contact stubs still at unknown) get the pending badge. */
export function isPendingDisposition(c: Contact): boolean {
  return c.status === "active" && c.createdBy === "first-contact-stub" && c.trustTier === "unknown";
}

/**
 * A blank OWNER draft, for the first-run case where `identity.get` reports
 * `owner: null` because the user has never completed an `identity.set`.
 *
 * Why this exists: the required-setup gate (`SetupGate`) holds a user on
 * `/identity` until the blocking `owner-name` step is done, and US-INV-7 says
 * the gate must contain its own exit. That exit is the name field on the "Você"
 * card — so when there is no owner row yet, the form still has to render
 * SOMETHING to type into. Seeding this draft is what makes the exit reachable;
 * without it the card fell through to its loading state and the gate had no
 * exit at all.
 *
 * `trustTier` is the D-7 owner tier because that is what the row IS, not a
 * guess. Nothing else here is invented: the fields the form actually persists
 * (name/treatment/language/emails/phones) all start empty, and the runtime
 * REJECTS a first `identity.set` with no `displayName`, so an untouched draft
 * can never be written.
 */
export function emptyOwnerDraft(): OwnerIdentityDraft {
  return { ...emptyContactDraft(), id: OWNER_CONTACT_ID, trustTier: "owner" };
}

/** New empty draft with the CT-INV-4 defaults. */
export function emptyContactDraft(): ContactDraft {
  return {
    kind: "person",
    displayName: "",
    phones: [],
    emails: [],
    channels: [],
    social: [],
    addresses: [],
    relationship: "unknown",
    tags: [],
    trustTier: "known",
    disposition: "notify-only",
    caseParties: [],
    status: "active",
  };
}
